<template>
  <div class="col-lg-12 col-md-12 col-xl-10 col-xxl-7">
    <section class="mb-5">
      <h1 class="h5 text-success mb-3">{{ $t("mis_datos.documentacion.seccion_usuario") }}</h1>
      <div class="documentacion-cards-grid">
          <MisDatosDocumentoDni
            ref="misDatosDocumentoDni"
            :document-uri="documentoDniAsociado()"
            @pending-file="onDniPendingFile"
          />
      </div>
    </section>

    <section v-if="puedeGestionarDocumentacionEmpresa" class="mt-4 pt-4 border-top">
      <h1 class="h5 text-success mb-3">{{ $t("mis_datos.documentacion.seccion_empresa") }}</h1>
      <p v-if="documentacionEmpresaSoloLectura" class="text-muted small mb-3">
        {{ $t("mis_datos.documentacion.empresa_solo_lectura") }}
      </p>
      <div v-if="!idEmpresaAgentes" class="text-muted">
        {{ $t("mis_datos.documentacion.sin_empresa_agentes") }}
      </div>
      <div v-else-if="cargandoEstructura" class="text-muted">{{ $t("mis_datos.documentacion.cargando") }}</div>
      <EmpresaAgentesDocumentacionPanel
        v-else-if="estructuraDocumental.length > 0"
        ref="documentacionEmpresaPanel"
        :id-empresa-agentes="idEmpresaAgentes"
        :estructura-documental="estructuraDocumental"
        sync-documentacion-externa
        :documentos-opcionales="documentacionEmpresaOpcional"
        :modo-edicion="documentacionEmpresaSoloLectura ? 'none' : 'all'"
      />
      <div v-else class="text-muted">{{ $t("mis_datos.documentacion.sin_tipos") }}</div>
    </section>

    <div class="mt-4 pt-3 border-top d-flex justify-content-end">
      <DxButton
        type="default"
        styling-mode="contained"
        :text="textoBotonGuardarDocumentacion"
        @click="guardarDocumentacion"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  EstadoEmpresaAgentesEnum,
  type EstructuraDocumentalEmpresaAgentesModel,
  type GetAgentePersonalDataModel,
} from "@/.shared/.api";
import { useApi } from "@/.shared/.api/integration";
import EmpresaAgentesDocumentacionPanel from "@components/empresa-agentes/EmpresaAgentesDocumentacionPanel.vue";
import MisDatosDocumentoDni from "@/components/mis-datos/MisDatosDocumentoDni.vue";
import { useAuthentication } from "@/.shared/services/auth";
import type { ExtendedUserInfo } from "@/.shared/services/auth/authentication/extended-user-info";
import { useAuthorization, PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import { useIntl } from "@/.shared/services/i18n";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { isDocumentacionEmpresaAgentesBloqueada } from "@/composables/empresaAgentesDocumentacionBloqueada";
import { recargarDatosEmpresaMisDatos } from "@/composables/misDatosRecargaEmpresa";
import { DxButton } from "devextreme-vue/button";
import { computed, nextTick, onMounted, ref, watch } from "vue";

const api = useApi();
const intl = useIntl();
const auth = useAuthentication();
const autho = useAuthorization();

const puedeGestionarDocumentacionEmpresa = computed(
  () =>
    autho.hasPermission(PermissionsApp.EditSameInmobiliariaAgente) ||
    autho.hasPermission(PermissionsApp.EditSameCompanyAgentes),
);

const idEmpresaAgentes = computed(() => auth.userInfo?.idEmpresaAgentes ?? null);

/** Empresas no autoregistro (siempre validadas): la estructura del API no exige obligatorios al guardar. */
const documentacionEmpresaOpcional = computed(() => {
  const user = auth.userInfo as ExtendedUserInfo | null;
  return !user?.autoregistroEmpresaAgentes;
});

const documentacionEmpresaSoloLectura = computed(() => {
  const user = auth.userInfo as ExtendedUserInfo | null;
  return isDocumentacionEmpresaAgentesBloqueada(
    user?.idEstadoEmpresaAgentes,
    user?.autoregistroEmpresaAgentes,
  );
});

const textoBotonGuardarDocumentacion = computed(() => {
  const user = auth.userInfo as ExtendedUserInfo | null;
  if (user?.idEstadoEmpresaAgentes === EstadoEmpresaAgentesEnum.Rechazada) {
    return intl.i18n.t("mis_datos.documentacion.btn_guardar_y_enviar_bo");
  }
  return intl.i18n.t("mis_datos.documentacion.btn_guardar");
});

const estructuraDocumental = ref<EstructuraDocumentalEmpresaAgentesModel[]>([]);
const cargandoEstructura = ref(false);

const model = ref<GetAgentePersonalDataModel | null>(null);
const documentoDniASubir = ref<File>();
const misDatosDocumentoDni = ref<InstanceType<typeof MisDatosDocumentoDni>>();
const documentacionEmpresaPanel = ref<InstanceType<typeof EmpresaAgentesDocumentacionPanel>>();

const documentoDniAsociado = (): string | undefined => {
  return model.value?.RutaDocumentoDni ?? undefined;
};

const onDniPendingFile = (file: File | undefined): void => {
  documentoDniASubir.value = file;
};

const cargarEstructuraEmpresa = async (): Promise<void> => {
  if (!puedeGestionarDocumentacionEmpresa.value || !idEmpresaAgentes.value) {
    estructuraDocumental.value = [];
    return;
  }
  cargandoEstructura.value = true;
  try {
    const data =
      (await api.callStandardApi("getEstructuraDocumentalEmpresaAgentes", { showLoading: false }, idEmpresaAgentes.value)) ?? [];
    estructuraDocumental.value = data;
  } finally {
    cargandoEstructura.value = false;
  }
};

const cargarDatosPersonales = async (): Promise<void> => {
  try {
    model.value = await api.callStandardApi("getCurrentAgentePersonalData", { showLoading: true });
  } catch {
    model.value = null;
  }
};

onMounted(async () => {
  await cargarDatosPersonales();
  await cargarEstructuraEmpresa();
});

watch([puedeGestionarDocumentacionEmpresa, idEmpresaAgentes], async () => {
  await cargarEstructuraEmpresa();
});

const guardarDocumentacion = async (): Promise<void> => {
  const dniValido = misDatosDocumentoDni.value?.isDocumentValid() ?? false;
  if (!dniValido) {
    toastError(intl.i18n.t("mis_datos.revise_validacion"));
    return;
  }

  const panel = documentacionEmpresaPanel.value;
  const hayPanelEmpresaEditable =
    !documentacionEmpresaSoloLectura.value &&
    !!panel &&
    puedeGestionarDocumentacionEmpresa.value &&
    estructuraDocumental.value.length > 0;

  if (hayPanelEmpresaEditable && !panel!.isDocumentacionValid()) {
    toastError(intl.i18n.t("empresa_agentes.documentacion.documento_validaciones_fallo"));
    return;
  }

  const hayDniPendiente = !!documentoDniASubir.value;
  const hayEmpresaPendiente = hayPanelEmpresaEditable && panel!.hayCambiosPendientesDocumentacion();

  if (!hayDniPendiente && !hayEmpresaPendiente) {
    toastSuccess(intl.i18n.t("mis_datos.documentacion.sin_cambios_pendientes"));
    return;
  }

  try {
    if (hayDniPendiente) {
      const m = model.value;
      if (!m?.IdUsuario) {
        toastError(intl.i18n.t("mis_datos.revise_validacion"));
        return;
      }
      const ficheroDni = documentoDniASubir.value!;
      const nombreArchivoDni = ficheroDni.name?.trim() || "documento";
      await api.callStandardApi(
        "putDocumentoAgente",
        { showLoading: true },
        m.IdUsuario,
        nombreArchivoDni,
        ficheroDni,
      );
      documentoDniASubir.value = undefined;
      model.value = await api.callStandardApi("getCurrentAgentePersonalData", { showLoading: false });
      await nextTick();
      misDatosDocumentoDni.value?.resetAfterSave();
    }

    if (hayEmpresaPendiente) {
      await panel!.sincronizarCambiosDocumentacion(undefined, { suprimirToastExito: true });
      await cargarEstructuraEmpresa();
    }

    await recargarDatosEmpresaMisDatos();
    toastSuccess(intl.i18n.t("mis_datos.documentacion.guardar_exito"));
  } catch {
    toastError(intl.i18n.t("mis_datos.actualizar_documento_fallo"));
  }
};
</script>

<style lang="scss" scoped>
.documentacion-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  width: 100%;
}
</style>
